import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input, ViewEncapsulation } from '@angular/core';
import { WLTempBorderDirective } from '../../directives/wl-temp-border.directive';
import { TailwindService } from '../../services/tailwind-service.service';
import { TailwindService as TW } from '@seven1/angular-storyblok/tailwind';
import { FirstPresentValuePipe } from '../../../../pipes/first-present-value.pipe';
import { ImageUrlTransformPipe } from '../../pipes/image-url-transform.pipe';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { KameleoonService } from '@seven1/angular/kameleoon';
import { IHeroSectionBlok } from './hero-section.model';
import { CmsComponent } from '@seven1/angular-storyblok-cms';

@Component({
    selector: 'app-hero-section-container',
    imports: [CommonModule, CmsComponent, WLTempBorderDirective, FirstPresentValuePipe, ImageUrlTransformPipe],
    templateUrl: './hero-section-container.component.html',
    styleUrls: ['./hero-section-container.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: {
        '[class]': 'getDisplayClasses()',
    },
})
export class HeroSectionContainerComponent {
    public readonly baseClass: string = 'app-hero-section-container';
    private classes: TailwindService = inject(TailwindService);
    private _tailwind = inject(TW);
    private kameleoonService = inject(KameleoonService);
    private route = inject(ActivatedRoute);

    block = input.required<IHeroSectionBlok>();

    queryParams = toSignal(this.route.queryParamMap);
    visible = computed(() => {
        return this.kameleoonService.visible(this.queryParams(), this.block());
    });

    getOuterClasses = computed(() => {
        const blok = this.block();
        return [
            ...this._tailwind.getSpacingClasses(blok),
            this.classes.getBackgroundSizeClass(blok.backgroundSize || 'cover'),
            this.classes.getBackgroundPositionClassFunction({
                bgPosition: blok.bgPosition,
                bgPositionTablet: blok.bgPositionTablet,
                bgPositionDesktop: blok.bgPositionDesktop,
            }),
            this.getBackgroundClass(),
        ].join(' ');
    });
    getInnerClasses = computed(() => {
        const blok = this.block();
        return [
            ...this._tailwind.getSpacingClasses(blok),
            this.classes.getGradientClassesFunction({
                gradientDirection: blok.gradientDirection,
                gradientDirectionTablet: blok.gradientDirectionTablet,
                gradientDirectionDesktop: blok.gradientDirectionDesktop,
            }),
            this.getGradientColorClass(),
        ].join(' ');
    });

    getGradientColorClass = computed(() => {
        const blok = this.block();
        return blok.gradientColor === 'primary'
            ? 'from-primary-gradient-start to-primary-gradient-end text-primary-contrast'
            : blok.gradientColor === 'accent'
            ? 'from-accent-gradient-start to-accent-gradient-end text-accent-contrast'
            : '';
    });

    getBackgroundClass = computed(() => {
        const blok = this.block();
        switch (blok.bgColor) {
            case 'primary':
                return 'bg-primary text-primary-contrast';
            case 'secondary':
                return 'bg-secondary text-secondary-contrast';
            case 'accent':
                return 'bg-accent text-accent-contrast';
            case 'grey':
                return 'bg-grey text-grey-contrast';
            default:
                return '';
        }
    });

    getDisplayClasses = computed(() => {
        const blok = this.block();
        return this._tailwind.getDisplayClasses(blok).join(' ');
    });

    transformMobileImage = computed(() => {
        const blok = this.block();
        return !blok.backgroundImageTablet && !blok.backgroundImageDesktop;
    });
}
